/**
 * The county waitlist and the "we're open in your county" email (docs/WAITLIST.md).
 *
 * Owner, 2026-10-05: "Make a waitlist, collect email of people who try to sign up outside of Salt Lake, and let them know when
 * we flip the switch via a beautiful email." Then: "Website should be open after launch. They should be able to make an account."
 *
 * So anyone, anywhere, makes an account and sets up their business. What waits for their county is STARTING THE PAID PLAN (Checkout).
 * A business in a county that isn't open sees "Coming to <County>" where the trial would start, with one unticked box: "Email me when
 * CleanCleaver opens in <County>". When the owner opens that county (admin, never automatic), each person on that county's list gets ONE
 * email: "<County> is open: your CleanCleaver account is ready".
 *
 * Everything here is pure: the server (services/api/src/waitlist/) and the web app read the same words and rules.
 */
import { countyIn, countyLabel } from "./states/index.ts";
import { isSupportedState, type UsState } from "./states/types.ts";
import { TRIAL_PERIOD_DAYS } from "./payments.ts";
import { leadDate } from "./leads.ts";
import { PILOT_COUNTIES } from "./states/availability.ts";

/* ─────────────── where CleanCleaver is open ─────────────── */

/** A county as one key: "UT:Salt Lake" (the state, a colon, the county as the state pack spells it). */
export type CountyKey = `${string}:${string}`;

/**
 * The code default: the pilot (states/availability.ts). The server adds OPEN_COUNTIES (env, replaces this list when set) and the
 * counties an admin opened (kept in the database), so opening a county is a config or admin change, never a code change.
 */
export const DEFAULT_OPEN_COUNTIES: readonly CountyKey[] = PILOT_COUNTIES;

/** "UT", "salt lake county" → "UT:Salt Lake"; null when the state isn't one we cover or the county isn't one of its counties. */
export function countyKey(state: string | null | undefined, county: string | null | undefined): CountyKey | null {
  const st = (state ?? "").trim().toUpperCase();
  if (!isSupportedState(st)) return null;
  const c = countyIn(st as UsState, county);
  return c ? `${st}:${c}` : null;
}

export function splitCountyKey(key: string): { state: UsState; county: string } | null {
  const i = key.indexOf(":");
  if (i < 0) return null;
  const k = countyKey(key.slice(0, i), key.slice(i + 1));
  return k ? { state: k.slice(0, 2) as UsState, county: k.slice(3) } : null;
}

/**
 * OPEN_COUNTIES: "UT:Salt Lake, NV:Clark". Unset or blank = the code default. Entries that aren't a real county are dropped (and
 * reported in `bad`) rather than opening something by accident.
 */
export function parseOpenCounties(env: string | undefined): { keys: CountyKey[]; bad: string[] } {
  if (!env?.trim()) return { keys: [...DEFAULT_OPEN_COUNTIES], bad: [] };
  const keys: CountyKey[] = [], bad: string[] = [];
  for (const raw of env.split(",").map((s) => s.trim()).filter(Boolean)) {
    const k = splitCountyKey(raw);
    if (k) { const key = `${k.state}:${k.county}` as CountyKey; if (!keys.includes(key)) keys.push(key); } else bad.push(raw);
  }
  return { keys, bad };
}

/** What a business's county means for starting the paid plan. */
export interface CountyStatus {
  /** True: Checkout may start. Homes are free while we test, so they are never held. */
  open: boolean;
  home?: boolean;
  state: UsState | null;
  /** As the pack spells it ("Salt Lake"), or null when no place has a county yet ("Not sure yet"). */
  county: string | null;
  /** "Salt Lake County". */
  countyLabel: string | null;
  /** The place the county came from (to set a county when it was "Not sure yet"). */
  locationId: string | null;
}

/**
 * Open when ANY of the business's places is in an open county (the plan covers every place; one open place is enough to start).
 * The status names the first place with a county, else the first place.
 */
export function countyStatusOf(
  org: { kind: string; state?: string | null },
  locations: readonly { id: string; county?: string | null; state?: string | null }[],
  open: ReadonlySet<string>,
): CountyStatus {
  if (org.kind === "home") return { open: true, home: true, state: null, county: null, countyLabel: null, locationId: null };
  const placed = locations.map((l) => ({ l, key: countyKey(l.state ?? org.state ?? "UT", l.county) }));
  const hit = placed.find((p) => p.key && open.has(p.key));
  const first = hit ?? placed.find((p) => p.key) ?? placed[0];
  const key = first?.key ? splitCountyKey(first.key) : null;
  const st = (first?.l.state ?? org.state ?? "").toUpperCase();
  return {
    open: !!hit,
    state: key?.state ?? (isSupportedState(st) ? (st as UsState) : null),
    county: key?.county ?? null,
    countyLabel: key ? countyLabel(key.county) : null,
    locationId: first?.l.id ?? null,
  };
}

/* ─────────────── the words ─────────────── */

/** The wording version kept with each consent (bump it when the box's words change). */
export const WAITLIST_CONSENT_VERSION = "2026-10-05.1";
/** The box, unticked, and required to join. */
export const waitlistConsentText = (label: string) => `Email me when CleanCleaver opens in ${label}`;

/** "Salt Lake County": the first county where we're open (the copy says where we started, from the data, never a literal). */
export const firstOpenLabel = (open: readonly string[] = DEFAULT_OPEN_COUNTIES): string => {
  const k = open.length ? splitCountyKey(open[0]!) : null;
  return k ? countyLabel(k.county) : "one county";
};

/** The hold card where "Start your 25 free days" would be. Owner-as-hero; nothing here blocks the free parts of the app. */
export function waitlistHoldWords(label: string | null, first: string = firstOpenLabel()) {
  const where = label ?? "your county";
  return {
    title: `Coming to ${where}`,
    lede: `We're opening county by county. We're in ${first} first.`,
    body: `Your kitchen stays set up. Your ${TRIAL_PERIOD_DAYS} free days start once we open in ${where}, so none of them go by while you wait.`,
    pickCounty: "Which county is your kitchen in?",
    action: "Save my spot",
    done: (masked: string) => `You're on the list for ${where}. We'll email ${masked} once, the day we open.`,
    meanwhile: "Until then, these are yours to use:",
    library: "Read the food safety library",
    find: `See ${where}'s inspection results`,
  };
}

/* ─────────────── the launch email ─────────────── */

export interface LaunchEmailInput {
  /** As the pack spells it. */
  county: string;
  state: UsState;
  /** True: they already have an account (the button starts their own Checkout). False: a sign-up link with the county filled in. */
  hasAccount: boolean;
  /** Where the button goes (built by the server: their own business's /billing/start, or sign-up). */
  actionUrl: string;
  /** When they ticked the box (the footer says so). */
  consentAt: string;
  unsubscribeUrl: string;
  /** The physical postal address (CAN-SPAM, 15 U.S.C. 7704(a)(5)(A)(iii)). The server refuses to send without one. */
  postalAddress: string;
  /** The county's inspection results page on our site, only when we show that county's results today. */
  findUrl?: string;
  /** The square app icon, an absolute https URL (shown with alt text; the email reads fine with images off). */
  logoUrl: string;
}

export interface LaunchEmail { subject: string; preheader: string; text: string; html: string; headers: { name: string; value: string }[] }

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** The brand palette (docs/brand/README.md), light and dark. Every pair is AA: ink on paper 15.3:1, white on water 5.6:1. */
const C = { paper: "#FBFAF6", card: "#FFFFFF", ink: "#17232E", ink2: "#46545F", water: "#0A7090", waterTint: "#E4F4F9", rule: "#E4E1D6" };
const D = { paper: "#0F1820", card: "#17232E", ink: "#F3F1EA", ink2: "#C9D1D7", water: "#5CC8E8", rule: "#2C3B47" };

/**
 * One function makes the preview, the test send and every real send, so what the admin reads is what people get (only the
 * unsubscribe link and the button's own address differ per person).
 */
export function launchEmail(i: LaunchEmailInput): LaunchEmail {
  const label = countyLabel(i.county);
  const subject = i.hasAccount ? `${label} is open: your CleanCleaver account is ready` : `CleanCleaver is open in ${label}`;
  const preheader = i.hasAccount ? `Your ${TRIAL_PERIOD_DAYS} free days can start today.` : `Set up your kitchen in a few minutes.`;
  const heading = `CleanCleaver is open in ${label}`;
  const lines = i.hasAccount
    ? [`You set up your kitchen while we were opening county by county. Today we opened in ${label}, so your ${TRIAL_PERIOD_DAYS} free days can start.`,
      "Your fridge temperatures, your binder for the inspector and your team are already where you left them."]
    : [`You asked us to tell you when we opened in ${label}. We're open today.`,
      "Set up your kitchen, and your fridge temperatures and your binder for the inspector start filling in from the first day."];
  const findLine = i.findUrl ? `We already have ${label}'s health inspection results. Look up any place:` : null;
  const action = i.hasAccount ? `Start your ${TRIAL_PERIOD_DAYS} free days` : "Set up your kitchen";
  const after = i.hasAccount
    ? `You add your card on Stripe's secure page. Cancel before day ${TRIAL_PERIOD_DAYS} and you pay nothing.`
    : `Your ${TRIAL_PERIOD_DAYS} free days start when you add a card. Cancel before day ${TRIAL_PERIOD_DAYS} and you pay nothing.`;
  const why = `You asked us to email you when we opened in ${label} (you ticked the box on ${leadDate(i.consentAt)}). This is the only email about it.`;

  const text = [
    heading, "",
    ...lines.flatMap((l) => [l, ""]),
    ...(findLine ? [`${findLine} ${i.findUrl}`, ""] : []),
    `${action}: ${i.actionUrl}`, "",
    after, "",
    "Zak Kline, CleanCleaver", "",
    "--",
    why,
    `Unsubscribe: ${i.unsubscribeUrl}`,
    i.postalAddress,
  ].join("\n");

  const p = (s: string, extra = "") => `<p style="margin:0 0 16px;font-size:16px;line-height:1.55;color:${C.ink}${extra}" class="cc-ink">${s}</p>`;
  const html = `<!doctype html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="x-apple-disable-message-reformatting">
<meta name="color-scheme" content="light dark">
<meta name="supported-color-schemes" content="light dark">
<title>${esc(subject)}</title>
<!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
<style>
:root{color-scheme:light dark;supported-color-schemes:light dark}
body{margin:0;padding:0;width:100%!important;-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%}
a{color:${C.water}}
@media (max-width:620px){.cc-pad{padding:24px 20px!important}.cc-h1{font-size:26px!important}.cc-btn a{display:block!important}}
@media (prefers-color-scheme:dark){
.cc-bg{background:${D.paper}!important}.cc-card{background:${D.card}!important;border-color:${D.rule}!important}
.cc-ink{color:${D.ink}!important}.cc-ink2{color:${D.ink2}!important}.cc-link{color:${D.water}!important}
.cc-btn-cell{background:${D.water}!important}.cc-btn a{background:${D.water}!important;color:${D.card}!important;border-color:${D.water}!important}
}
[data-ogsc] .cc-ink{color:${D.ink}!important}[data-ogsc] .cc-ink2{color:${D.ink2}!important}
</style>
</head>
<body class="cc-bg" style="margin:0;padding:0;background:${C.paper}">
<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:${C.paper};opacity:0">${esc(preheader)}&#8199;&#847;&#8199;&#847;&#8199;&#847;&#8199;&#847;&#8199;&#847;</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" class="cc-bg" style="background:${C.paper}">
<tr><td align="center" style="padding:24px 12px">
<!--[if mso]><table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0"><tr><td><![endif]-->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;margin:0 auto">
<tr><td style="padding:0 4px 16px">
<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
<td style="vertical-align:middle"><img src="${esc(i.logoUrl)}" width="40" height="40" alt="CleanCleaver" style="display:block;border:0;border-radius:10px;width:40px;height:40px"></td>
<td style="vertical-align:middle;padding-left:10px;font-family:system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;color:${C.ink}" class="cc-ink">CleanCleaver</td>
</tr></table>
</td></tr>
<tr><td class="cc-card cc-pad" style="background:${C.card};border:1px solid ${C.rule};border-radius:16px;padding:32px 36px;font-family:system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif">
<h1 class="cc-h1 cc-ink" style="margin:0 0 16px;font-family:Georgia,'Times New Roman',serif;font-size:30px;line-height:1.2;font-weight:700;color:${C.ink}">${esc(heading)}</h1>
${lines.map((l) => p(esc(l))).join("\n")}
${findLine ? p(`${esc(findLine)} <a href="${esc(i.findUrl!)}" class="cc-link" style="color:${C.water};font-weight:700">${esc(`${label} results`)}</a>`) : ""}
<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 16px" class="cc-btn"><tr><td align="center" class="cc-btn-cell" style="border-radius:14px;background:${C.water}">
<!--[if mso]><v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${esc(i.actionUrl)}" style="height:56px;v-text-anchor:middle;width:320px" arcsize="25%" stroke="f" fillcolor="${C.water}"><w:anchorlock/><center style="color:#FFFFFF;font-family:Arial,sans-serif;font-size:18px;font-weight:bold">${esc(action)}</center></v:roundrect><![endif]-->
<!--[if !mso]><!--><a href="${esc(i.actionUrl)}" style="display:inline-block;background:${C.water};color:#FFFFFF;text-decoration:none;font-size:18px;font-weight:700;line-height:24px;padding:16px 28px;border-radius:14px;border:2px solid ${C.water};min-width:220px;text-align:center">${esc(action)}</a><!--<![endif]-->
</td></tr></table>
<p class="cc-ink2" style="margin:0 0 24px;font-size:15px;line-height:1.5;color:${C.ink2}">${esc(after)}</p>
<p class="cc-ink" style="margin:0;font-size:16px;line-height:1.5;color:${C.ink}">Zak Kline, CleanCleaver</p>
</td></tr>
<tr><td style="padding:20px 8px 0;font-family:system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif">
<p class="cc-ink2" style="margin:0 0 8px;font-size:14px;line-height:1.5;color:${C.ink2}">${esc(why)} <a href="${esc(i.unsubscribeUrl)}" class="cc-link" style="color:${C.water}">Unsubscribe</a> and we stop at once.</p>
<p class="cc-ink2" style="margin:0;font-size:14px;line-height:1.5;color:${C.ink2}">${esc(i.postalAddress)}</p>
</td></tr>
</table>
<!--[if mso]></td></tr></table><![endif]-->
</td></tr>
</table>
</body>
</html>`;
  return {
    subject, preheader, text, html,
    headers: [
      { name: "List-Unsubscribe", value: `<${i.unsubscribeUrl}>` },
      { name: "List-Unsubscribe-Post", value: "List-Unsubscribe=One-Click" },
    ],
  };
}
